import type { IDirective, ITimeoutService, IWindowService } from "angular";
import { module } from "angular";

import { flatten, head, isNil, last, map, orderBy, union, uniq, without } from "lodash";
import type { IScreenService } from "App/Modules/Services";

type MouseEventBase = JQuery.MouseEventBase;

const INTERVALS_COUNT = 144;
const INTERVAL_LENGTH = 10;
const INTERVAL_HEIGHT = 10;
const SUB_INTERVAL_HEIGHT = 2;

export default class RangeDirective implements IDirective {
    private static drawIntervals(element: JQuery, intervalsCount: number, intervalHeight: number) {
        for (let i = 0; i < intervalsCount; i++) {
            const rect = RangeDirective.createSVG("rect", {
                class: "interval",
                "data-number": i,
                height: `${intervalHeight}px`,
                width: `${INTERVAL_LENGTH}px`,
                x: i * INTERVAL_LENGTH,
                y: 0,
            });

            element[0].append(rect);
        }

        for (let i = 0; i < intervalsCount; i++) {
            const dash = RangeDirective.createSVG("line", {
                class: i % 6 === 0 && i !== 0 && i !== intervalsCount - 1 ? "thick-dash" : "dash",
                x1: i * INTERVAL_LENGTH,
                x2: i * INTERVAL_LENGTH,
                y1: 0,
                y2: intervalHeight,
            });

            element[0].append(dash);
        }
    }

    private static updateSelected(
        element: JQuery,
        newInterval: number[],
        intervals: number[][],
        selectingMode: boolean,
    ) {
        const newIntervals = RangeDirective.normalize(intervals, newInterval, selectingMode);

        RangeDirective.drawSelected(element, newIntervals);

        return newIntervals;
    }

    private static drawSelected(element: JQuery, intervals: number[][]) {
        const intervalsElements = element.find(".interval");

        intervalsElements.toggleClass("is-selected", false);

        intervals.forEach((interval) => {
            for (let i = head(interval); i <= last(interval); i++) {
                intervalsElements.eq(i).toggleClass("is-selected", true);
            }
        });
    }

    private static createSVG(tag: string, attributes: any) {
        const element = document.createElementNS("http://www.w3.org/2000/svg", tag);

        // TODO: Rewrite below (see lint error)
        // eslint-disable-next-line no-restricted-syntax
        for (const key in attributes) {
            if (attributes.hasOwnProperty(key)) {
                element.setAttribute(key, attributes[key]);
            }
        }

        return element;
    }

    private static getIntervalOnPosition(element: JQuery, absolutePosition: number, intervalsCount: number) {
        const position = absolutePosition - element.offset().left;
        const interval = Math.floor((position / element.width()) * intervalsCount);
        const [min, max] = [0, INTERVALS_COUNT - 1];

        return Math.min(max, Math.max(min, interval));
    }

    private static normalize(intervals: number[][], newInterval: number[], mode: boolean) {
        const plain = map(intervals, (i) => RangeDirective.fromIntervalToPlain(i));
        const flat = flatten(plain);
        const filtered = uniq(flat);

        const withNewOne = orderBy(
            mode
                ? union(filtered, RangeDirective.fromIntervalToPlain(newInterval))
                : without(filtered, ...RangeDirective.fromIntervalToPlain(newInterval)),
        );

        const newIntervals: number[][] = [];

        withNewOne.forEach((interval) => {
            if (!newIntervals.length) {
                newIntervals.push([interval]);
            } else if (last(newIntervals).length === 1 && last(newIntervals)[0] === interval - 1) {
                last(newIntervals).push(interval);
            } else if (last(newIntervals).length === 2 && last(newIntervals)[1] === interval - 1) {
                last(newIntervals)[1] = interval;
            } else {
                newIntervals.push([interval]);
            }
        });

        return newIntervals;
    }

    private static fromIntervalToPlain(interval: number[]) {
        const plain = [];
        const sortedInterval = orderBy(interval);

        for (let i = head(sortedInterval); i <= last(sortedInterval); i++) {
            plain.push(i);
        }

        return plain;
    }

    private static moveTooltip(
        event: MouseEventBase,
        potentialModal: JQuery,
        tooltip: JQuery,
        value: string[],
        isMSIE: boolean,
    ) {
        const inner = tooltip.find(".tooltip-inner");
        const text = uniq(value).join(" - ");

        if (potentialModal.length > 0 && !isMSIE) {
            const relativeX = event.pageX - potentialModal.offset().left;
            const relativeY = event.pageY - potentialModal.offset().top;

            inner.text(text);

            tooltip.css({
                display: "block",
                left: relativeX,
                top: relativeY,
            });
        } else {
            inner.text(text);

            tooltip.css({
                display: "block",
                left: event.clientX,
                top: event.clientY,
            });
        }
    }

    private static hideTooltip(tooltip: JQuery) {
        tooltip.css("display", "none");
    }

    public restrict = "A";

    public scope = {
        intervals: "=",
        intervalsCount: "<",
        isDisabled: "<",
        staticIntervals: "<",
        tooltipMapper: "<",
    };

    constructor(
        private readonly $timeout: ITimeoutService,
        private readonly $window: IWindowService,
        private readonly screenService: IScreenService,
    ) {}

    public link($scope: any, element: JQuery) {
        $scope.selectingMode = true;
        $scope.currentInterval = [];
        $scope.intervals = $scope.intervals || [];
        $scope.intervalsCount = $scope.intervalsCount || INTERVALS_COUNT;

        const tooltip = element.find("#tooltip");
        const potentialModal = element.parents(".modal-dialog");
        const isMSIE = this.screenService.isMSIE();
        const intervalsElement = element.find("#intervals");
        const isSubIntervalAvailable = !isNil($scope.staticIntervals);

        const intervalHeight = isSubIntervalAvailable ? INTERVAL_HEIGHT - SUB_INTERVAL_HEIGHT : INTERVAL_HEIGHT;
        RangeDirective.drawIntervals(intervalsElement, $scope.intervalsCount, intervalHeight);

        if (isSubIntervalAvailable) {
            $scope.$watch("staticIntervals", (newValue: number[][]) => {
                RangeDirective.drawIntervals(
                    element.find("#sub-intervals"),
                    $scope.intervalsCount,
                    SUB_INTERVAL_HEIGHT,
                );
                RangeDirective.drawSelected(element.find("#sub-intervals"), newValue);
            });
        }

        $scope.$watch("intervals", (newValue: any) => {
            RangeDirective.updateSelected(intervalsElement, $scope.currentInterval, newValue, $scope.selectingMode);
        });

        function onDrag(event: MouseEventBase) {
            $scope.currentInterval = [
                $scope.currentInterval[0],
                RangeDirective.getIntervalOnPosition(element, event.pageX, $scope.intervalsCount),
            ];

            RangeDirective.moveTooltip(
                event,
                potentialModal,
                tooltip,
                $scope.tooltipMapper($scope.currentInterval),
                isMSIE,
            );

            RangeDirective.updateSelected(
                intervalsElement,
                $scope.currentInterval,
                $scope.intervals,
                $scope.selectingMode,
            );
        }

        function onDragEnd(event: MouseEventBase) {
            $scope.currentInterval = [
                $scope.currentInterval[0],
                RangeDirective.getIntervalOnPosition(element, event.pageX, $scope.intervalsCount),
            ];

            $scope.$apply(() => {
                $scope.intervals = RangeDirective.updateSelected(
                    intervalsElement,
                    $scope.currentInterval,
                    $scope.intervals,
                    $scope.selectingMode,
                );
            });

            $(document).off("mousemove", onDrag);
            $(document).off("mouseup", onDragEnd);

            if (!$((event as any).target).closest(element).length) {
                $(document).off("mousemove", onHover);
            }

            RangeDirective.hideTooltip(tooltip);

            element.hover(onHoverIn, onHoverOut);
        }

        function onHoverIn(event: MouseEventBase) {
            const interval = RangeDirective.getIntervalOnPosition(element, event.pageX, $scope.intervalsCount);

            RangeDirective.moveTooltip(event, potentialModal, tooltip, $scope.tooltipMapper([interval]), isMSIE);

            $(document).on("mousemove", onHover);
        }

        function onHover(event: MouseEventBase) {
            const interval = RangeDirective.getIntervalOnPosition(element, event.pageX, $scope.intervalsCount);

            RangeDirective.moveTooltip(event, potentialModal, tooltip, $scope.tooltipMapper([interval]), isMSIE);
        }

        function onHoverOut() {
            RangeDirective.hideTooltip(tooltip);

            $(document).off("mousemove", onHover);
        }

        element.on("mousedown", (event: MouseEventBase) => {
            if ($scope.isDisabled) {
                return;
            }

            $(document).on("mousemove", onDrag);
            $(document).on("mouseup", onDragEnd);

            element.off("mouseenter mouseleave");

            $scope.currentInterval = [
                RangeDirective.getIntervalOnPosition(element, event.pageX, $scope.intervalsCount),
            ];

            $scope.selectingMode = !element
                .find(`#intervals>[data-number="${$scope.currentInterval[0]}"]`)
                .hasClass("is-selected");

            RangeDirective.moveTooltip(
                event,
                potentialModal,
                tooltip,
                $scope.tooltipMapper($scope.currentInterval),
                isMSIE,
            );
            RangeDirective.updateSelected(
                intervalsElement,
                $scope.currentInterval,
                $scope.intervals,
                $scope.selectingMode,
            );
        });

        element.hover(onHoverIn, onHoverOut);
    }
}

module("mmGui").directive("irRange", [
    "$timeout",
    "$window",
    "irScreenService",
    ($timeout: ITimeoutService, $window: IWindowService, irScreenService: IScreenService) =>
        new RangeDirective($timeout, $window, irScreenService),
]);
